import type { MetaFunction } from "react-router"

import { ButtonLink } from "~/components/ui/button"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { WrittenBy } from "~/components/blog/WrittenBy"
import { A, H2, H3, Lead, P } from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// Kjartan on privacy as a product principle: aggregation at the source,
// then thresholds, differential privacy and rounding in the published
// statistics, so customers only ever receive aggregated location
// intelligence. Copy from KJ's Google Doc (2026-10-23). Published 2026-10-23.

const LINKS = {
  privacy:
    "https://console.pine59.com/docs/concepts/techniques/privacy-approach",
  ali: "/blog/location-data-vs-location-intelligence",
}

const post = getPost("privacy-by-design")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          Location intelligence helps businesses and organizations understand
          how physical places are used. How many people visit a shopping area?
          Where do visitors come from? How do movement patterns change over
          time?
        </Lead>
        <P>
          Answering these questions requires mobility data. And working with
          mobility data comes with an important responsibility: protecting the
          privacy of the people behind the observations.
        </P>
        <P>
          At Pine59, privacy is not something we add at the end of the process.
          It shapes the data we work with, how we process it, and what we make
          available to customers.
        </P>

        <H2>Privacy starts with the data source</H2>
        <P>
          Pine59's products are built on pre-aggregated first-party data,
          regardless of the underlying signal source. The aggregation we apply
          removes all personally identifiable information and is designed so the
          data can never be traced back to an individual.
        </P>
        <P>
          Whether the original observations come from mobile applications,
          telecommunications networks, or other sources, individual signals are
          aggregated before becoming part of Pine59's data foundation.
        </P>
        <P>
          We then apply quality checks, validation, and statistical modeling to
          produce estimates of activity across places, areas, and time periods.
        </P>
        <P>
          This allows us to estimate visitation to a shopping center, compare
          activity between neighborhoods, or analyze movement patterns between
          geographic areas, without accessing individual location histories.
        </P>
        <P>
          Our focus is not on understanding where individual people go, but on
          understanding how places and areas are used by populations.
        </P>

        <H2>Privacy protection in the data itself</H2>
        <P>Aggregation is the foundation, but it is not the only safeguard.</P>
        <P>
          Depending on the country, data source, and product, Pine59 applies
          additional techniques to protect privacy while preserving the
          statistical value of the data.
        </P>

        <H3>Minimum thresholds and K filtering</H3>
        <P>
          Some statistics are only published when they represent a sufficiently
          large group of observations.
        </P>
        <P>
          When a group is too small, the result is suppressed rather than
          published. This reduces the risk of individual contributions being
          isolated in small datasets.
        </P>

        <H3>Differential privacy</H3>
        <P>
          For certain datasets, carefully calibrated statistical noise is
          introduced to reduce the risk of information about individuals being
          inferred from published statistics.
        </P>
        <P>
          The aim is to preserve meaningful patterns across populations while
          providing additional protection for individual privacy.
        </P>

        <H3>Rounding</H3>
        <P>
          Published statistics may also be rounded, providing another layer of
          protection against overly precise reporting.
        </P>

        <P>
          These safeguards are applied according to the characteristics of each
          dataset and <A href={LINKS.privacy}>methodology</A>. Not every
          technique is used in every product, but the underlying principle
          remains the same: useful statistical insights should not come at the
          expense of individual privacy.
        </P>

        <H2>What customers actually receive</H2>
        <P>
          Pine59 customers receive aggregated statistical insights through our
          dashboards, APIs, and data services.
        </P>
        <P>
          Our products are designed to answer questions about places and
          populations, not to identify or follow individual people.
        </P>
        <P>
          Whether customers explore data through Pine59 Insights, integrate it
          into their own systems, or use it in AI workflows, the same principle
          applies: the output is{" "}
          <A href={LINKS.ali}>aggregated location intelligence</A>, not
          individual mobility data.
        </P>

        <H2>Privacy as a product principle</H2>
        <P>
          Privacy is often discussed as a compliance requirement. For location
          intelligence, it is also a fundamental product-design decision.
        </P>
        <P>
          We believe organizations can make better decisions about cities,
          retail locations, infrastructure, and public spaces without knowing
          the movements of individual people.
        </P>
        <P>The value lies in understanding collective patterns.</P>
        <P>
          That is why Pine59 is built around statistical insights about places
          and populations, with privacy considerations extending from the
          original data source to the final product.
        </P>
        <div className="mt-8">
          <ButtonLink href="/get-started" withArrow>
            Build on Pine59
          </ButtonLink>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
